import { Component, OnInit, NgZone, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { MyOffersService } from '../../services/my-offers.service';
import { Offer } from '../../models/offers.model';
import { Observable } from 'rxjs';
import { User, OccEndpointsService } from '@spartacus/core'; // adjust if you use a different User type
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-offer-page',
    templateUrl: './offer-page.component.html',
    styleUrls: ['./offer-page.component.scss']
    // If you set changeDetection: ChangeDetectionStrategy.OnPush here,
    // the code below (markForCheck) is essential.
    ,
    standalone: false
})
export class OfferPageComponent implements OnInit {
  private readonly occEndpointService = inject(OccEndpointsService);

  offer: Offer | null = null;
  isLoading = true;
  error: string | null = null;
  unavailableForLocation = false;
  isInactive = false;
  readonly unavailableMessageKey = 'myOffers.emptyState.detail';
  readonly inactiveMessageKey = 'myOffers.emptyState.inactive';

  // Get backend base URL for media document links (same as My Offers page)
  readonly baseurl = this.occEndpointService.getBaseUrl({
    baseUrl: true,
    prefix: false,
    baseSite: false,
  });

  // Expose user$ because the template uses it
  user$: Observable<User | undefined>;

  constructor(
    public route: ActivatedRoute,
    private myOffersService: MyOffersService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.user$ = this.myOffersService.user$;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const offerId = params.get('offerId');
      console.log('[OfferPageComponent] offerId from route:', offerId);

      if (offerId) {
        this.fetchOffer(offerId);
      } else {
        this.error = 'No offer id in route';
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onOfferAccepted(evt: unknown): void {
    console.log('[OfferPageComponent] offerAccepted', evt);
  }

  private fetchOffer(offerId: string): void {
    console.log('[OfferPageComponent] fetchOffer() called - resetting flags');
    this.isLoading = true;
    this.error = null;
    this.offer = null;
    this.unavailableForLocation = false;
    this.isInactive = false;

    // Directly fetch the offer by ID - let the backend handle eligibility logic
    // This allows accepted but deactivated offers to still be displayed
    this.loadOfferDetails(offerId);
  }

  private loadOfferDetails(offerId: string): void {
    this.myOffersService
      .getOfferById(offerId)
      .pipe(
        finalize(() => {
          // Ensure spinner stops even if an error happens
          this.ngZone.run(() => {
            this.isLoading = false;
            console.log('[OfferPageComponent] finalize() - isLoading set to false, unavailableForLocation=', this.unavailableForLocation);
            this.cdr.markForCheck(); // Important if OnPush or out-of-zone emission
          });
        })
      )
      .subscribe({
        next: (data: Offer | null) => {
          console.log('[OfferPageComponent] received data:', data, 'Type:', typeof data);
          this.ngZone.run(() => {
            // Backend returns 200 with OfferWsDto for all cases:
            // - Active and eligible: active=true, isEligible=true
            // - Active but ineligible: active=true, isEligible=false
            // - Inactive but accepted (eligible): active=false, isEligible=true (show offer - they accepted before deactivation)
            // - Inactive and not accepted: active=false, isEligible=false (show inactive message)
            // - Not found: 404 (handled in error callback)
            if (data === null) {
              // Should not happen - backend always returns OfferWsDto now
              console.warn('[OfferPageComponent] Received null data - unexpected');
              this.unavailableForLocation = false;
              this.isInactive = false;
              this.offer = null;
              this.error = null;
            } else if (data.isEligible === true) {
              // Offer is eligible (either active or inactive but accepted) - show it
              // This includes: active=true OR (active=false but accepted before deactivation)
              console.log('[OfferPageComponent] Received eligible offer - active={}, isEligible={}', data.active, data.isEligible);
              this.offer = data;
              this.unavailableForLocation = false;
              this.isInactive = false;
            } else if (data.active === false) {
              // Offer exists but is inactive and not eligible (not accepted before deactivation)
              console.log('[OfferPageComponent] Received offer with active=false and isEligible=false - offer is inactive');
              this.isInactive = true;
              this.unavailableForLocation = false;
              this.offer = null; // Don't show offer data when inactive
              this.error = null; // Template will show 'myOffers.emptyState.inactive'
            } else {
              // Offer exists and is active but not eligible for this location
              // (active=true, isEligible=false)
              console.log('[OfferPageComponent] Received offer with active=true but isEligible=false - offer exists but not eligible for location');
              this.unavailableForLocation = true;
              this.isInactive = false;
              this.offer = null; // Don't show offer data when not eligible
              this.error = null; // Template will show 'myOffers.emptyState.detail'
            }
            this.cdr.markForCheck();
          });
        },
        error: (err: HttpErrorResponse | Error) => {
          console.error('[OfferPageComponent] error fetching offer:', err);
          this.ngZone.run(() => {
            // Only 404 errors should reach here (for truly not found offers - doesn't exist in system)
            // Inactive and ineligible offers now return 200 with OfferWsDto, handled in next() callback
            if (err instanceof HttpErrorResponse && err.status === 404) {
              // Offer not found (doesn't exist in system or not linked to site)
              console.log('[OfferPageComponent] Handling 404 - offer not found in system');
              this.unavailableForLocation = false;
              this.isInactive = false;
              this.offer = null;
              this.error = null; // Template will show 'myOffers.emptyState.notFound'
            } else {
              // Other HTTP errors (500, etc.) or network errors
              console.log('[OfferPageComponent] Handling other error:', err);
              this.error = err?.message || 'Failed to load offer details.';
              this.unavailableForLocation = false;
              this.isInactive = false;
            }
            this.cdr.markForCheck();
          });
        },
      });
  }
}
